import { inject, Inject, Injectable, Injector, Renderer2, RendererFactory2 } from '@angular/core';
import {
  ActiveConfig,
  AlertStyleConfigurationType,
  AlternativeStyleConfig,
  BaseCssConfig,
  BaseWidgetProperties,
  GlobalThemeConfiguration,
  LogosResultTypeList,
  NestedWorkflowWidgetConfiguration,
  NewWorkflowWidgetConfig,
  RightWidgetConfiguration,
  StyleConfig,
  WidgetNames,
  WorkflowPageConfig,
  WorkflowWidgetConfiguration,
  WorkflowWidgetConfigurationAttributes,
  WorkflowZoneConfig,
  WorkflowZoneDevicesConfig,
  cryptoRandomUUID,
  findWidget,
  getWidgetConfigForReset,
  getWidgetsArrayByLayoutTypeOrWidgetId,
  pruneBaseCssConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { BehaviorSubject, Observable, map, take } from 'rxjs';
import { RemoveWidget } from '../../actions/app-grid.action';
import { AppGridState } from '../../state/app-grid.state';
import { ProjectsState } from '../../state/project.state';
import { ActiveConfigService } from '../active-config/active-config.service';
import { ApiService } from '../api-service/api.service';
import { AppBuilderChangesStatusService } from '../app-builder-changes-service/app-builder-changes-status.service';
import { WorkflowEditorService } from '../workflow-editor/workflow-editor.service';

import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { PageConfig, buildDynamicZoneConfig } from '@finxone-platform/shared/workflow-mapping';
import { WorkflowConfigService } from './workflow-config.service';

/**
 * @description it uses for making common
 * functions inside workflow and reduce the repeated code,
 * it can have any common function
 * which is in used more than one place
 */

@Injectable({ providedIn: 'root' })
export class WorkflowCommonService {
  activeConfig!: ActiveConfig;
  activeViewScreen = '';
  @Select(AppGridState.getVisibleWidgetConfig)
  widgetConfigItems$: Observable<RightWidgetConfiguration[]>;
  widgetConfigItems: RightWidgetConfiguration[];
  private imageLibraryDataSource = new BehaviorSubject<LogosResultTypeList>({
    filename: '',
    path: '',
    id: '',
    global_status: false,
  });
  imageLibraryData$ = this.imageLibraryDataSource.asObservable();
  private renderer: Renderer2;

  private injector = inject(Injector); // Lazy injection via getter to avoid circular dependency with WorkflowEditorService
  private _workflowEditorService?: WorkflowEditorService;
  private get workflowEditorService(): WorkflowEditorService {
    return (this._workflowEditorService ??= this.injector.get(WorkflowEditorService));
  }

  constructor(
    private workflowConfigService: WorkflowConfigService,
    private activeConfigService: ActiveConfigService,
    private store: Store,
    private apiService: ApiService,
    private appBuilderChangesStatusService: AppBuilderChangesStatusService,
    private actions$: Actions,
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
  ) {
    this.actions$.pipe(ofActionDispatched(RemoveWidget)).subscribe((obj) => {
      this.removeWidgetPageConfig(obj.widget.index, obj.widget.uniqueId);
    });

    this.widgetConfigItems$.subscribe((rhsWidgetConfig) => {
      this.widgetConfigItems = rhsWidgetConfig;
    });

    this.activeConfigService.getConfig().subscribe((config: ActiveConfig) => {
      this.activeConfig = config;
      this.activeViewScreen = this.activeConfig.activeViewScreen.page;
    });
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  updateFieldAddtionalConfigWidget<T>(widgetConfigValue: {
    uniqueId: string;
    addConfig: T;
    widgetName: WidgetNames;
  }): void {
    this.onUpdatePageConfig((pageConfig) => {
      updateWidgetById(pageConfig.widgets, widgetConfigValue.uniqueId, (widget) => {
        const fieldAttributes = widget.widgetConfiguration.attributes['field_attributes'] || [];
        const fieldAttributeIndex = fieldAttributes.findIndex(
          (addConfig: { name: string }) => addConfig?.name === widgetConfigValue.widgetName,
        );

        if (fieldAttributeIndex !== -1) {
          fieldAttributes[fieldAttributeIndex] = {
            ...fieldAttributes[fieldAttributeIndex],
            additional_config: widgetConfigValue.addConfig,
          };
        } else {
          fieldAttributes.push({
            name: widgetConfigValue.widgetName,
            additional_config: widgetConfigValue.addConfig,
          });
        }

        widget.widgetConfiguration.attributes['field_attributes'] = fieldAttributes;
      });

      return pageConfig;
    });
  }

  getAnySectionAttributes(uniqueId: string) {
    const pageConfig = this.getActiveWorkflowPageConfig();
    const widget = findWidget(uniqueId, pageConfig.widgets);
    if (widget) {
      return widget.widgetConfiguration.attributes;
    } else {
      throw Error('No widget found');
    }
  }

  updateWidgetConfigAttribute<T>(widgetConfigValue: {
    uniqueId: string;
    attributes: T;
    isReset?: boolean;
  }): void {
    this.onUpdatePageConfig((pageConfig) => {
      updateWidgetById(pageConfig.widgets, widgetConfigValue.uniqueId, (widget) => {
        widget.widgetConfiguration.attributes =
          widgetConfigValue.attributes as WorkflowWidgetConfigurationAttributes;
      });
      return pageConfig;
    });
  }

  public updateNestedWidgetConfiguration(widgetConfig: {
    uniqueId: string;
    nestedWidgets: { [key: string]: NestedWorkflowWidgetConfiguration };
  }): void {
    this.onUpdatePageConfig((pageConfig) => {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, undefined, widgetConfig.uniqueId);
      const widget = findWidget(widgetConfig.uniqueId, widgets);
      if (widget) {
        widget.widgetConfiguration.nestedWidgets = widgetConfig?.nestedWidgets;
      }
      return pageConfig;
    });
  }

  onGetActivePageConfigByIndex(): WorkflowPageConfig {
    const pageConfig = this.getActiveWorkflowPageConfig();
    return pageConfig;
  }

  public updateWidgetProperties(widgetConfigValue: {
    uniqueId: string | number;
    widgetProperties: BaseWidgetProperties;
    isReset?: boolean;
  }): void {
    this.onUpdatePageConfig((pageConfig) => {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(
        pageConfig,
        undefined,
        widgetConfigValue.uniqueId,
      );
      updateWidgetById(widgets, widgetConfigValue.uniqueId, (widget) => {
        widget.widgetConfiguration.attributes.widgetProperties = widgetConfigValue.widgetProperties;
      });
      return pageConfig;
    });
  }

  updateConfigWidget<T>(
    widgetConfigValue: {
      uniqueId: string;
      addConfig: T;
      isReset?: boolean;
    },
    nestedWidget: {
      isNested: boolean;
      uniqueId: string;
    } = { isNested: false, uniqueId: '' },
  ): void {
    this.onUpdatePageConfig((pageConfig) => {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(
        pageConfig,
        undefined,
        widgetConfigValue.uniqueId,
      );
      const widget = findWidget(widgetConfigValue.uniqueId, widgets);
      if (widget) {
        if (widgetConfigValue.isReset) {
          this.resetWidgetConfigNew(widget.name as WidgetNames, widget.widgetConfiguration);
        } else {
          const nestedWidgetConfig = widget.widgetConfiguration?.nestedWidgets?.[nestedWidget.uniqueId];
          // css panel emits the full ~60-field object on every change; only
          // store deliberate overrides — defaults are restored during
          // workflow -> system mapping (hydrateBaseCssConfig)
          const prunedCssConfig = pruneBaseCssConfig(widgetConfigValue.addConfig as BaseCssConfig);
          if (nestedWidget.isNested && nestedWidgetConfig) {
            nestedWidgetConfig.attributes.css_config = prunedCssConfig;
          } else {
            widget.widgetConfiguration.attributes.css_config = prunedCssConfig;
          }
        }
      }
      return pageConfig;
    });
  }

  updateWidgetConfigWithNestedWidget(widgetConfig: {
    uniqueId: string;
    nestedConfigKey: string;
    value: RightWidgetConfiguration;
  }): void {
    this.onUpdatePageConfig((pageConfig) => {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(pageConfig, undefined, widgetConfig.uniqueId);
      const widget = findWidget(widgetConfig.uniqueId, widgets);
      if (widget) {
        const existingWidgetConfig =
          widget.widgetConfiguration?.nestedWidgets?.[widgetConfig.nestedConfigKey] ??
          widget.widgetConfiguration;

        const newWidgetConfig = widgetConfig?.value;

        const updatedAttributes: WorkflowWidgetConfigurationAttributes = {
          ...existingWidgetConfig.attributes,
          ...newWidgetConfig?.attributes,
          css_config: {
            ...existingWidgetConfig.attributes?.css_config,
            ...newWidgetConfig?.attributes?.css_config,
          },
          widgetProperties: {
            ...existingWidgetConfig.attributes?.widgetProperties,
            ...newWidgetConfig?.attributes?.widgetProperties,
          },
        };

        if (widget.widgetConfiguration?.nestedWidgets?.[widgetConfig.nestedConfigKey]) {
          widget.widgetConfiguration.nestedWidgets[widgetConfig.nestedConfigKey] = {
            ...(existingWidgetConfig as NestedWorkflowWidgetConfiguration),
            attributes: updatedAttributes,
          };
        } else {
          widget.widgetConfiguration = {
            ...(existingWidgetConfig as WorkflowWidgetConfiguration),
            attributes: updatedAttributes,
          };
        }
      }

      return pageConfig;
    });
  }

  public updateGlobalStyleWidgetConfig(
    widgetConfigValue: {
      uniqueId: string;
      widgetAttributes: WorkflowWidgetConfigurationAttributes;
    },
    key: string,
  ): void {
    this.onUpdatePageConfig((pageConfig) => {
      let widget = findWidget(widgetConfigValue.uniqueId, pageConfig.widgets);
      if (widget) {
        const nestedWidgets = widget.widgetConfiguration.nestedWidgets ?? {};
        const nestedWidget = nestedWidgets[key];
        if (nestedWidget?.attributes.widgetProperties?.['globalStyling'] === true) {
          /* Resetting all other CSS configurations taking only icon related config */
          // css_config may be pruned to overrides-only (pruneBaseCssConfig), so a
          // default-valued icon field reads as undefined here — reuse pruneBaseCssConfig
          // to drop those undefined entries instead of persisting an explicit `key:
          // undefined`, which would survive hydrateBaseCssConfig's spread later and
          // break its default fallback
          const currentCssConfig = nestedWidget.attributes.css_config ?? {};
          nestedWidget.attributes.css_config = pruneBaseCssConfig({
            iconUnit: currentCssConfig.iconUnit,
            iconSize: currentCssConfig.iconSize,
            iconColor: currentCssConfig.iconColor,
            leftIconColor: currentCssConfig.leftIconColor,
            leftIconUnit: currentCssConfig.leftIconUnit,
            rightIconUnit: currentCssConfig.rightIconUnit,
            rightIconSize: currentCssConfig.rightIconSize,
            rightIconColor: currentCssConfig.rightIconColor,
            leftIconSize: currentCssConfig.leftIconSize,
          } as unknown as BaseCssConfig);
        } else {
          /* Resetting only the button's CSS configuration with default Rest config */
          if (nestedWidget.name === WidgetNames.BUTTON) {
            const widgetConfig = getWidgetConfigForReset[WidgetNames.WIDGET_NESTED_BUTTON];
            Object.assign(nestedWidget?.attributes.css_config ?? {}, widgetConfig?.attributes.css_config);
          }
        }

        widget = {
          ...widget,
          widgetConfiguration: {
            ...widget.widgetConfiguration,
            nestedWidgets: {
              ...nestedWidgets,
              [key]: {
                ...(nestedWidgets[key] ?? {}),
                attributes: {
                  ...(nestedWidgets[key]?.attributes ?? {}),
                  widgetProperties: {
                    ...(nestedWidgets[key]?.attributes?.widgetProperties ?? {}),
                    ...widgetConfigValue.widgetAttributes.widgetProperties,
                  },
                },
              },
            },
          },
        };
        pageConfig.widgets = this.updateWidgetConfig(pageConfig.widgets, widget);
      }
      return pageConfig;
    });
  }

  updateWidgetConfig(
    widgets: NewWorkflowWidgetConfig[],
    updatedWidget: NewWorkflowWidgetConfig,
  ): NewWorkflowWidgetConfig[] {
    return widgets.map((widget) => {
      // If the widget's uniqueId matches, replace it
      if (widget.uniqueId.toString() === updatedWidget.uniqueId.toString()) {
        return { ...updatedWidget };
      }

      // If widget has child components, update them recursively
      if (widget.childComponents) {
        return {
          ...widget,
          childComponents: Array.isArray(widget.childComponents[0])
            ? (widget.childComponents as NewWorkflowWidgetConfig[][]).map((childArray) =>
                this.updateWidgetConfig(childArray, updatedWidget),
              )
            : this.updateWidgetConfig(widget.childComponents as NewWorkflowWidgetConfig[], updatedWidget),
        };
      }

      return widget;
    });
  }

  updatePageConfiguration(workflowPage: WorkflowPageConfig): void {
    this.onUpdatePageConfig((pageConfig) => {
      pageConfig.pageConfiguration = workflowPage.pageConfiguration;
      return pageConfig;
    });
  }

  copyPageProperties(workflowPage: WorkflowPageConfig): void {
    this.onUpdatePageConfig((pageConfig) => {
      pageConfig.pageConfiguration = {
        ...workflowPage.pageConfiguration,
        mainComponentPageName: pageConfig.pageConfiguration.mainComponentPageName,
        isRemovable: pageConfig.pageConfiguration.isRemovable,
        url: pageConfig.pageConfiguration.url,
        pageType: pageConfig.pageConfiguration.pageType,
      };
      return pageConfig;
    });
  }

  public updateWidgetAttributes(widgetConfigValue: {
    uniqueId: string | number;
    widgetAttributes: WorkflowWidgetConfigurationAttributes;
  }): void {
    this.onUpdatePageConfig((pageConfig) => {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(
        pageConfig,
        undefined,
        widgetConfigValue.uniqueId,
      );
      updateWidgetById(widgets, widgetConfigValue.uniqueId, (widget) => {
        widget.widgetConfiguration.attributes = widgetConfigValue.widgetAttributes;
      });
      return pageConfig;
    });
  }

  /**
   * Updates the `skeletonPreviewData` attribute for all Text widgets
   * in the current page configuration that already contain `skeletonPreviewData`.
   *
   * It merges the existing preview data with the provided `skeletonPreviewData`
   *
   * @param skeletonPreviewData - The new data to merge into existing `skeletonPreviewData`.
   */
  public updateSiblingsWidgetAttributes<T>(skeletonPreviewData: T): void {
    this.onUpdatePageConfig((pageConfig) => {
      pageConfig.widgets.forEach((widget) => {
        const isTextWidget = widget?.name === WidgetNames.TEXT_WIDGET;
        const hasSkeletonData = widget.widgetConfiguration.attributes?.widgetProperties?.skeletonPreviewData;

        if (isTextWidget && hasSkeletonData) {
          const existingAttributes = widget.widgetConfiguration.attributes;
          const existingWidgetProperties = existingAttributes?.widgetProperties;

          widget.widgetConfiguration.attributes = {
            ...existingAttributes,
            widgetProperties: {
              ...existingWidgetProperties,
              skeletonPreviewData: {
                ...existingWidgetProperties?.skeletonPreviewData,
                ...skeletonPreviewData,
                id: cryptoRandomUUID() + widget.widgetConfiguration?.widgetConfigDisplayName,
              },
            },
          };
        }
      });

      return pageConfig;
    });
  }

  public updateAndRestwidgetConfigStyle(
    widgetConfigValue: {
      uniqueId: string | number;
      widgetAttributes: WorkflowWidgetConfigurationAttributes;
    },
    widgetName: string,
  ) {
    const { widgetAttributes } = widgetConfigValue;
    const widgetConfig = getWidgetConfigForReset[widgetName];

    if (widgetAttributes.widgetProperties?.['globalStyling'] === true) {
      /* Resetting all other CSS configurations taking only icon related config */
      widgetAttributes.css_config = {
        iconUnit: widgetConfigValue.widgetAttributes.css_config?.iconUnit,
        iconSize: widgetConfigValue.widgetAttributes.css_config.iconSize,
        iconColor: widgetConfigValue.widgetAttributes.css_config?.iconColor,
        leftIconColor: widgetConfigValue.widgetAttributes.css_config?.leftIconColor,
        leftIconUnit: widgetConfigValue.widgetAttributes.css_config?.leftIconUnit,
        rightIconUnit: widgetConfigValue.widgetAttributes.css_config?.rightIconUnit,
        rightIconSize: widgetConfigValue.widgetAttributes.css_config?.rightIconSize,
        rightIconColor: widgetConfigValue.widgetAttributes.css_config?.rightIconColor,
        leftIconSize: widgetConfigValue.widgetAttributes.css_config?.leftIconSize,
      };
    } else {
      /* Resetting only the CSS configuration with default Rest config */
      Object.assign(widgetAttributes.css_config, widgetConfig?.attributes.css_config);
    }

    this.onUpdatePageConfig((pageConfig) => {
      const widgets = getWidgetsArrayByLayoutTypeOrWidgetId(
        pageConfig,
        undefined,
        widgetConfigValue.uniqueId,
      );
      updateWidgetById(widgets, widgetConfigValue.uniqueId, (widget) => {
        widget.widgetConfiguration.attributes = widgetAttributes;
      });

      return pageConfig;
    });
  }

  private resetWidgetConfigNew(
    pageName: WidgetNames,
    widgetConfiguration: WorkflowWidgetConfiguration,
  ): WorkflowWidgetConfiguration {
    const widgetConfig = getWidgetConfigForReset[pageName];

    // reset default wins over existing; the panel's addConfig (its full form
    // state) is deliberately NOT merged — it re-added every field on every
    // reset, so css_configs only ever grew
    const attributesConfig = {
      ...widgetConfig.attributes,
      css_config: pruneBaseCssConfig({
        ...(widgetConfiguration?.attributes as any)?.css_config,
        ...widgetConfig?.attributes?.css_config,
      }),
    };

    const widgetProperties = {
      widgetProperties: {
        ...widgetConfiguration?.attributes?.widgetProperties,
        ...widgetConfig?.attributes?.widgetProperties,
      },
    };

    (widgetConfig?.elementsConfig || widgetConfig?.attributes?.elementsConfig)?.forEach((element, index) => {
      const elementConfig = widgetConfiguration?.attributes.elementsConfig?.[index];

      if (element.field_name === elementConfig.field_name) {
        elementConfig.show = element?.show;
      }

      if (element.field_name === elementConfig.field_name && element?.placeholder) {
        elementConfig.placeholder = element?.placeholder;
      }

      elementConfig.type = element?.type;

      if (element?.timer) {
        elementConfig.timer = element?.timer;
      }

      if (element?.options?.length) {
        element?.options?.forEach((option, optionIndex) => {
          elementConfig.options[optionIndex].show = option?.show;
        });
      } else {
        elementConfig.options = element?.options;
      }
    });

    widgetConfig?.attributes?.field_attributes?.forEach(
      (element: { additional_config: unknown }, fieldIndex: number) => {
        (widgetConfiguration.attributes as any).field_attributes[fieldIndex].additional_config =
          element?.additional_config;
      },
    );

    widgetConfiguration.attributes = {
      ...attributesConfig,
      ...widgetProperties,
      elementsConfig: widgetConfiguration?.attributes?.elementsConfig,
    };

    return widgetConfiguration;
  }

  updateRHSWorkflowConfig(rhsConfigData: WorkflowWidgetConfigurationAttributes, uniqueId: string) {
    this.onUpdatePageConfig((pageConfig) => {
      updateWidgetById(pageConfig.widgets, uniqueId, (widget) => {
        widget.widgetConfiguration.attributes = rhsConfigData;
      });

      return pageConfig;
    });
  }

  /**
   * @description:
   * this method is used to update Active permission sections
   */
  onUpdatePageConfig(fn: (pageConfig: WorkflowPageConfig) => WorkflowPageConfig) {
    this.workflowConfigService.updateConfig((config) => {
      const zones =
        config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones;

      const defaultSubscription = zones[this.activeConfigService.activeState.activeZone]?.canvases
        ?.subscription?.[
        this.activeConfigService.activeState.activeCanvasSubscription
      ] as WorkflowZoneDevicesConfig;

      const pageConfig =
        defaultSubscription[this.activeConfigService.activeState.activeDevice]?.pages[
          this.activeConfigService.activeState.activePageIndex
        ];

      if (pageConfig) {
        fn(pageConfig);
      }
      return config;
    });
  }

  private onUpdateZoneConfig(fn: (pageConfig: WorkflowZoneConfig) => WorkflowZoneConfig) {
    this.workflowConfigService.updateConfig((config) => {
      const zones =
        config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones;

      const defaultSubscription = zones[this.activeConfigService.activeState.activeZone]?.canvases
        ?.subscription?.[
        this.activeConfigService.activeState.activeCanvasSubscription
      ] as WorkflowZoneDevicesConfig;

      const zoneConfig = defaultSubscription[this.activeConfigService.activeState.activeDevice];

      if (zoneConfig) {
        fn(zoneConfig);
      }
      return config;
    });
  }

  /**
   * @description:
   * this method is used to get Active permission sections
   */

  getActiveWorkflowPageConfig(pageIndexOverride?: number): WorkflowPageConfig {
    let configSectionType: WorkflowPageConfig = {} as WorkflowPageConfig;
    const pageIndex = pageIndexOverride ?? this.activeConfigService.activeState.activePageIndex;
    this.workflowConfigService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const zones =
          config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones;

        const defaultSubscription = zones[this.activeConfigService.activeState.activeZone]?.canvases
          ?.subscription?.[
          this.activeConfigService.activeState.activeCanvasSubscription
        ] as WorkflowZoneDevicesConfig;

        const pageConfig =
          defaultSubscription?.[this.activeConfigService.activeState.activeDevice]?.pages[pageIndex];
        if (pageConfig) {
          configSectionType = pageConfig;
        }
      });

    return configSectionType;
  }

  private getWidgetName(uniqueId: string | number) {
    let widgetName = '';
    this.workflowConfigService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const zoneConfig =
          config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones[
            this.activeConfigService.activeState.activeZone
          ]?.canvases.subscription[this.activeConfigService.activeState.activeCanvasSubscription];

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        const pageConfig = (zoneConfig as WorkflowZoneDevicesConfig)[
          this.activeConfigService.activeState.activeDevice
        ]?.pages[this.activeConfigService.activeState.activePageIndex]!;

        const widget = pageConfig.widgets.find((widget) => widget.uniqueId === uniqueId);
        if (widget) widgetName = widget.name;
      });
    return widgetName;
  }

  private getImageWidgetIds(uniqueId: string | number): string[] {
    let imageIds: string[] = [];
    this.workflowConfigService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const zoneConfig =
          config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones[
            this.activeConfigService.activeState.activeZone
          ]?.canvases.subscription[this.activeConfigService.activeState.activeCanvasSubscription];

        const page = (zoneConfig as WorkflowZoneDevicesConfig)[
          this.activeConfigService.activeState.activeDevice
        ]?.pages[this.activeConfigService.activeState.activePageIndex];
        const widgetIndex = page?.widgets.findIndex((widget) => widget.uniqueId === uniqueId);
        if (widgetIndex) {
          const widget = page?.widgets[widgetIndex];
          if (widget?.name === WidgetNames.IMAGE_WIDGET) {
            const options = (
              page?.widgets?.[widgetIndex]?.widgetConfiguration as any
            )?.elementsConfig?.flatMap((item: { options: any }) => item.options);

            imageIds = options?.map((field: any) => field.id).filter((id: any) => id !== undefined);
          }
        }
        return [];
      });
    return imageIds;
  }

  removeWidget(uniqueId: string | number) {
    const widgetName = this.getWidgetName(uniqueId);
    if (widgetName === WidgetNames.IMAGE_WIDGET) {
      const imageIds = this.getImageWidgetIds(uniqueId);
      this.removeImageFiles(imageIds);
    }

    this.workflowEditorService.removeWidgetFromPage(
      uniqueId,
      undefined,
      this.activeConfig.activeZone,
      this.activeConfig.activePageIndex,
    );
  }

  removeWidgetPageConfig(index: number, uniqueId: string | number): void {
    this.removeWidget(uniqueId);
  }

  private removeImageFiles(ids: string[]): void {
    if (ids?.length > 0) {
      const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;

      const imagePayload = {
        id: ids,
        projectId: projectId,
      };

      this.apiService.deleteCall(`/file`, imagePayload).subscribe();
    }
    this.appBuilderChangesStatusService.appBuilderStatus = 'true';
  }

  removeLastAddedWidget() {
    this.workflowConfigService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const zoneConfig =
          config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones[
            this.activeConfigService.activeState.activeZone
          ]?.canvases.subscription[this.activeConfigService.activeState.activeCanvasSubscription];

        let obj: {
          index: number;
          uniqueId: string | number;
          screenName: string;
          isWidgetPage?: boolean;
        };
        const defaultSubscription = zoneConfig as WorkflowZoneDevicesConfig;
        const page =
          defaultSubscription[this.activeConfig.activeDevice]?.pages[this.activeConfig.activePageIndex];
        if (page) {
          const index = page?.widgets.length - 1;
          const widgetToRemove = page?.widgets[index];
          obj = {
            index: index,
            screenName: widgetToRemove.widgetConfiguration.widgetConfigDisplayName,
            uniqueId: widgetToRemove.uniqueId,
          };
        } else {
          return;
        }
        this.store.dispatch(new RemoveWidget(obj));
      });
  }

  getCurrentZoneSectionsOrPageConfig(): Observable<WorkflowZoneDevicesConfig> {
    return this.workflowConfigService.getConfig().pipe(
      take(1),
      map((config) => {
        const zoneConfig =
          config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones[
            this.activeConfigService.activeState.activeZone
          ]?.canvases.subscription[this.activeConfigService.activeState.activeCanvasSubscription];

        return zoneConfig as WorkflowZoneDevicesConfig;
      }),
    );
  }

  private updateCurrentZoneConfig(fn: (zone: WorkflowZoneDevicesConfig) => WorkflowZoneDevicesConfig) {
    this.workflowConfigService.updateConfig((config) => {
      const zones =
        config.permissionSections.roles[this.activeConfigService.activeState.activeRoleIndex].zones[
          this.activeConfigService.activeState.activeZone
        ];

      if (zones) {
        zones.canvases.subscription[this.activeConfigService.activeState.activeCanvasSubscription] = fn(
          zones.canvases.subscription[
            this.activeConfigService.activeState.activeCanvasSubscription
          ] as WorkflowZoneDevicesConfig,
        );
      }

      return config;
    });
  }

  updateImageWidgetConfig(uniqueId: string | number, response: any, fieldName: WidgetNames, files?: File[]) {
    this.updateCurrentZoneConfig((data) => {
      this.workflowEditorService.updateImageWidgetConfig(uniqueId, response, fieldName, files, data);

      return data;
    });
  }

  public updateAlertToasterConfigurationWorkflowConfig(alertStyle: AlertStyleConfigurationType) {
    this.workflowConfigService.updateConfig((config) => {
      config.alertsConfig = { ...config.alertsConfig, ...alertStyle };
      return config;
    });
  }

  public updateImageLibraryData(data: LogosResultTypeList) {
    this.imageLibraryDataSource.next(data);
  }

  public updateSystemInfoAndAlternativeThemeData(globalThemeData: GlobalThemeConfiguration) {
    this.workflowConfigService.updateConfig((config) => {
      if (config) {
        const workflowStyleConfig = config.styleConfig;
        if (workflowStyleConfig) {
          const workflowConfigAlternativeThemes = config.styleConfig.alternativeThemes;
          if (workflowConfigAlternativeThemes) {
            /**
             * Get the lowercase-theme-name (hyphenated) for finding the key
             */
            const activeGlobalThemeName = formatNameAsUrl(globalThemeData?.themeName?.toLowerCase());
            const matchingAlternativeTheme = Object.keys(workflowConfigAlternativeThemes).find(
              (v) => v?.toLowerCase() === activeGlobalThemeName,
            );
            if (matchingAlternativeTheme) {
              workflowConfigAlternativeThemes[matchingAlternativeTheme] =
                this.updateSystemInfoFromGlobalTheme(
                  workflowConfigAlternativeThemes[matchingAlternativeTheme],
                  globalThemeData.generalConfigurations,
                  activeGlobalThemeName,
                );
            } else {
              this.updateSystemInfoFromGlobalTheme(
                workflowStyleConfig,
                globalThemeData.generalConfigurations,
                activeGlobalThemeName,
              );
            }
            config.styleConfig.alternativeThemes = workflowConfigAlternativeThemes;
          } else {
            this.updateSystemInfoFromGlobalTheme(workflowStyleConfig, globalThemeData.generalConfigurations);
          }
        }
      }
      return config;
    });
  }

  public addNewAlternativeThemeConfig(
    globalThemeData: StyleConfig | AlternativeStyleConfig,
    themeKey: string,
  ) {
    this.workflowConfigService.updateConfig((config) => {
      if (config) {
        config.styleConfig.alternativeThemes = config.styleConfig.alternativeThemes || {};
        config.styleConfig.alternativeThemes[themeKey] = globalThemeData;
      }
      return config;
    });
  }

  private updateSystemInfoFromGlobalTheme(
    config: StyleConfig | AlternativeStyleConfig,
    generalConfigurations: GlobalThemeConfiguration['generalConfigurations'],
    activeGlobalThemeName = '',
  ): StyleConfig | AlternativeStyleConfig {
    /**
     * Need to modify alternate theme as well when you change the active them from that,
     * somewhere in the app builder, it takes list of alternate themes so it has to be up to date with active * theme if it matches.
     */
    if (activeGlobalThemeName && config.alternativeThemes?.[activeGlobalThemeName]) {
      config.alternativeThemes = {
        ...config?.alternativeThemes,
        [activeGlobalThemeName]: {
          ...config.alternativeThemes?.[activeGlobalThemeName],
          systemName: generalConfigurations.companyInfo.name,
          logo: generalConfigurations.companyInfo.logo,
          secondaryFontFamily: generalConfigurations.secondaryFontFamily,
        },
      };
    }
    config.legalEntityName = generalConfigurations.companyInfo.legalEntityName;
    config.systemName = generalConfigurations.companyInfo.name;
    config.logo = generalConfigurations.companyInfo.logo;
    config.logoTitle = generalConfigurations.companyInfo.logoTitle;
    if (config?.fontFamily) config.fontFamily = generalConfigurations.fontFamily;
    config['secondaryFontFamily'] = generalConfigurations.secondaryFontFamily;
    return config;
  }

  /**
   * Method to modify acitve zone pages when appp builder interacts with elementsConfig checkbox on right panel widget config
   * @param {PageConfig[]} pages - Pages to add.
   * @param {'add' | 'remove'} action - action to perform on zone pages.
   * @param {string[]} pageNames - Pages names which needs adding or removing.
   * @param {ActiveConfig} activeConfig - Active config.
   */
  public modifyZonePages(
    pages: PageConfig[],
    action: 'add' | 'remove',
    pageNames: string[],
    activeConfig: ActiveConfig,
  ) {
    this.workflowConfigService.updateConfig((config) => {
      const zones = config.permissionSections.roles[activeConfig.activeRoleIndex].zones;
      const defaultSubscription = zones[activeConfig.activeZone]?.canvases?.subscription?.[
        activeConfig.activeCanvasSubscription
      ] as WorkflowZoneDevicesConfig;
      const deviceConfig = defaultSubscription[activeConfig.activeDevice] as WorkflowZoneConfig;
      if (action === 'add') {
        // Add pages if they don't exist in the zone pages
        const pagesDataProcessed = buildDynamicZoneConfig(pages);
        pagesDataProcessed.forEach((page) => {
          const pageExists = deviceConfig.pages?.some((existingPage) => existingPage.name === page.name);

          if (!pageExists) {
            deviceConfig.pages = deviceConfig.pages || [];
            deviceConfig.pages.push(page);
          }
        });
      }
      if (action === 'remove') {
        // Remove pages from zone pages
        deviceConfig.pages = deviceConfig.pages?.filter((page) => !pageNames.includes(page.name));
      }
      return config;
    });
  }
}

// Utility function
export function updateWidgetById(
  widgets: NewWorkflowWidgetConfig[],
  uniqueId: string | number,
  updateFn: (widget: NewWorkflowWidgetConfig) => void,
): boolean {
  const widget = findWidget(uniqueId, widgets);
  if (widget) {
    updateFn(widget);
    return true;
  } else return false;
}
